// auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { throwError, Observable, BehaviorSubject, of } from 'rxjs';
import { UserStoreService } from '@core/services/storage/user/user-store.service';
import { Log } from '@core/services/config/app-config.service';

// Mutex per evitare chiamate multiple al refresh token
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(UserStoreService);

    // Aggiungiamo withCredentials a tutte le richieste per inviare i cookie
    const clonedReq = req.clone({
        withCredentials: true
    });

    // Escludiamo gli endpoint che possono restituire 401 in modo legittimo
    const isAuthEndpoint = clonedReq.url.includes('/login') ||
        clonedReq.url.includes('/register') ||
        clonedReq.url.includes('/refresh-token') ||
        clonedReq.url.includes('/user/profile');

    return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Se l'errore non è 401 o siamo su un endpoint di autenticazione, propaghiamo l'errore
            if (error.status !== 401 || isAuthEndpoint) {
                return throwError(() => error);
            }
            
            // Se è 401 e non stiamo già facendo un refresh, iniziamo il processo
            if (!isRefreshing) {
                isRefreshing = true;
                refreshTokenSubject.next(null);

                return authService.refreshToken().pipe(
                    switchMap(() => {
                        isRefreshing = false;
                        refreshTokenSubject.next('REFRESHED');

                        // Riprova la richiesta originale
                        return next(clonedReq);
                    }),
                    catchError((refreshError) => {
                        isRefreshing = false;

                        // In caso di errore nel refresh, logout
                        authService.logout();
                        return throwError(() => refreshError);
                    }),
                    finalize(() => {
                        isRefreshing = false;
                    })
                );
            } else {
                // Se è già in corso un refresh, attendiamo il completamento
                return refreshTokenSubject.pipe(
                    filter(token => token !== null),
                    take(1),
                    switchMap(() => {
                        // Riprova la richiesta originale quando il refresh è completato
                        return next(clonedReq);
                    })
                );
            }
        })
    );
};